import { CarPdfViewer } from "@app/components/PdfViewer";
import { CarPdfDocument } from "@app/components/PdfDocument";
import { CarPdfPage, defaultClientPageSize } from "@app/components/PdfPage";
import { useUser } from "@app/pages/settings/useUser";
import { CarPdfTypography } from "@app/components/PdfTypography";
import { SectorAnalysisPdfChart } from "@app/pages/assetClassAnalysis/pdfSectorAnalysisReport/SectorAnalysisPdfChart";
import { SectorAnalysisSummaryPdfTable } from "@app/pages/assetClassAnalysis/pdfSectorAnalysisReport/SectorAnalysisSummaryPdfTable";
import { SectorAnalysisFocusPdfTable } from "@app/pages/assetClassAnalysis/pdfSectorAnalysisReport/SectorAnalysisFocusPdfTable";
import { usePortfolioGroupInfo } from "@app/pages/assetAllocation/usePortfolioGroupInfo";
import { usePortfolioGroupVersionStatData } from "@app/pages/assetAllocation/usePortfolioGroupVersionStatData";
import { usePortfolioGroupVersionSectorSummary } from "@app/pages/assetAllocation/usePortfolioGroupVersionSectorSummary";

const SECTOR_ANALYSIS_CHART_HEIGHT = 470;

interface ModuleSectorAnalysisReportProps {
  portfolioGroupId: string;
  moduleId: string;
  data1Id: string;
  data2Id: string;
  sectorFocusId: string;
  footer: string;
}

export const ModuleSectorAnalysisReport = ({
  portfolioGroupId,
  moduleId,
  data1Id,
  data2Id,
  sectorFocusId,
  footer,
}: ModuleSectorAnalysisReportProps) => {
  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });

  const { portfolioGroupVersionId, publishedVersionId, isSinglePortfolioMode } =
    groupInfo;

  const statData = usePortfolioGroupVersionStatData({
    versionId: portfolioGroupVersionId,
    publishedVersionId,
    filterByModuleId: moduleId,
  });

  const sectorSummary = usePortfolioGroupVersionSectorSummary({
    portfolioGroupId,
    data1Id,
    data2Id,
  });

  const sectorFocusItem = sectorSummary.report.level1.find(
    (i) => i.id === sectorFocusId,
  );

  const user = useUser();

  const moduleName = statData.getModuleNameById(moduleId);

  const title = `Sector Analysis - ${isSinglePortfolioMode ? groupInfo.title : moduleName}`;

  return (
    <CarPdfViewer
      isLoading={user.isLoading || groupInfo.isLoading || statData.isLoading}
    >
      <CarPdfDocument title={title} author={user.userFullName}>
        {
          <CarPdfPage
            sectionTitle="Analyze"
            reportTitle={title}
            footer={footer}
          >
            <SectorAnalysisPdfChart
              style={{ marginTop: 18 }}
              width={defaultClientPageSize.width}
              height={SECTOR_ANALYSIS_CHART_HEIGHT}
              data={statData.multiPeriodReturnComparison}
              report={sectorSummary.report}
              data1Id={data1Id}
              data2Id={data2Id}
              isWhiteContext
            />
            <SectorAnalysisSummaryPdfTable
              break
              data={statData.multiPeriodReturnComparison}
              report={sectorSummary.report}
              data1Id={data1Id}
              data2Id={data2Id}
            />
            {sectorFocusItem && (
              <>
                <CarPdfTypography
                  style={{ marginTop: 28, marginBottom: 16 }}
                  variant="v14Semibold"
                  dense
                >
                  {`Sector Focus - ${sectorFocusItem.name}`}
                </CarPdfTypography>
                <SectorAnalysisFocusPdfTable
                  data={statData.multiPeriodReturnComparison}
                  sectorFocusItem={sectorFocusItem}
                  data1Id={data1Id}
                  data2Id={data2Id}
                />
              </>
            )}
          </CarPdfPage>
        }
      </CarPdfDocument>
    </CarPdfViewer>
  );
};
